import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Collapse, IconButton } from '@mui/material';
import { DataGridPro, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { Tool } from '@procureai/agents';
import { ExtendedList, ExtendedListItem, ExtendedListItemText, NestedList } from '@procureai/ui-mui';
import { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChatLoadingIndicator } from '../ChatMessage/indicators/ChatLoadingIndicator';
import { ChatToolResultIndicator } from '../ChatMessage/indicators/ChatToolResultIndicator';
import { SvbrgVetSuppliersInput, SvbrgVetSuppliersOutput } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useVetSuppliersResultDataGrid } from '~/routes/IntakeChat/components/VetSuppliersResult/VetSuppliersResult.dataGrid';

import { ToolResultComponent } from '.';

type SupplierDetailsProps = {
  tool: {
    input: SvbrgVetSuppliersInput;
  };
};

const SupplierDetails: React.FC<PropsWithChildren<SupplierDetailsProps>> = ({ children, tool }) => {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
  const { t: tCommon } = useTranslation(I18nNamespace.intake, {
    keyPrefix: 'tools.common',
  });

  return (
    <ExtendedList>
      <ExtendedListItem>
        {children}
        <IconButton
          size="small"
          onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
          aria-label={isDetailsExpanded ? tCommon('collapseDetails') : tCommon('expandDetails')}
        >
          {isDetailsExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </ExtendedListItem>
      <Collapse in={isDetailsExpanded}>
        <NestedList>
          {tool.input.supplierIds.map((supplierId, index) => {
            return (
              <ExtendedListItem key={index}>
                <ExtendedListItemText primary={`${tCommon('supplierID')}: ${supplierId}`} sx={{ mr: 2 }} />
              </ExtendedListItem>
            );
          })}
        </NestedList>
      </Collapse>
    </ExtendedList>
  );
};

export const VetSuppliersTool: ToolResultComponent<
  Tool.vetSuppliers,
  SvbrgVetSuppliersInput,
  SvbrgVetSuppliersOutput
> = ({ tool, toolName }) => {
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: `Chat.tools` });

  const config = useVetSuppliersResultDataGrid();
  const apiRef = useGridApiRef();

  switch (tool.state) {
    case 'input-available':
      return (
        <SupplierDetails tool={tool}>
          <ChatLoadingIndicator
            message={t(`${toolName}.call`, {
              count: tool.input.supplierIds.length,
            })}
          />
        </SupplierDetails>
      );

    case 'output-available': {
      const suppliers = tool.output?.suppliersResult || [];

      return (
        <ChatToolResultIndicator
          message={t(`common.completedVetting`, {
            count: suppliers.length,
          })}
          defaultOpen={true}
          disabled={false}
        >
          {suppliers.length > 0 && (
            <DataGridPro
              {...config}
              sx={{
                [`&.${gridClasses.root}`]: {
                  '--DataGrid-containerBackground': 'transparent',
                },
              }}
              apiRef={apiRef}
              disableColumnMenu
              rows={suppliers}
              getRowId={(row) => row.id}
            />
          )}
        </ChatToolResultIndicator>
      );
    }
  }
};
